var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/create-model.tsx
import React, {
  createContext,
  useContext,
  memo,
  useDebugValue,
  useEffect
} from "react";
import {
  useConstant
} from "@lyonph/react-hooks";

// src/notifier.tsx
var Notifier = class {
  constructor() {
    this.alive = true;
    this.initialized = false;
    this.listeners = new Set();
  }
  subscribe(callback) {
    if (this.alive) {
      this.listeners.add(callback);
    }
    return () => {
      if (this.alive) {
        this.listeners.delete(callback);
      }
    };
  }
  consume(value) {
    if (this.alive) {
      this.ref = {
        current: value
      };
      for (const listener of this.listeners.keys()) {
        listener(value);
      }
    }
  }
  hydrate(value) {
    if (this.alive && !this.initialized) {
      this.ref = {
        current: value
      };
    }
  }
  hasValue() {
    return !!this.ref;
  }
  get value() {
    if (this.ref == null) {
      throw new Error("Unexpected missing model reference.");
    }
    return this.ref.current;
  }
  destroy() {
    if (this.alive) {
      this.listeners.clear();
      this.alive = false;
    }
  }
};

// src/utils/MissingScopedModelError.tsx
var MissingScopedModelError = class extends Error {
  constructor(modelName) {
    super(`
  The scoped model '${modelName}' is missing from the ancestor component tree.
  Make sure that the model's Provider is mounted first before trying to access
  the model's state.
  `);
    this.modelName = modelName;
  }
};

// src/utils/id.ts
var ID = 0;
function generateId() {
  const current = ID;
  ID += 1;
  return current;
}

// src/create-model.tsx
function SHOULD_NOTIFY(a, b) {
  return !Object.is(a, b);
}
function createModel(useModelHook, options = {}) {
  var _a;
  const context = createContext(null);
  const id = generateId();
  const displayName = options.displayName || `ScopedModel-${id}`;
  const shouldNotify = (_a = options.shouldNotify) != null ? _a : SHOULD_NOTIFY;
  function useProcessor(props) {
    const emitter = useContext(context);
    if (!emitter) {
      throw new MissingScopedModelError(displayName);
    }
    const model = useModelHook(props);
    emitter.hydrate(model);
    useEffect(() => {
      emitter.initialized = true;
    }, [emitter]);
    useEffect(() => {
      const hasValue = emitter.hasValue();
      if (hasValue && shouldNotify(emitter.value, model) || !hasValue) {
        emitter.consume(model);
      }
    }, [emitter, model]);
    useDebugValue(model);
  }
  const ProcessorInner = (props) => {
    useProcessor(props);
    return null;
  };
  const Processor = memo(ProcessorInner, options.shouldUpdate);
  const Provider = (_b) => {
    var _c = _b, { children } = _c, props = __objRest(_c, ["children"]);
    const emitter = useConstant(() => new Notifier());
    useEffect(() => () => {
      emitter.destroy();
    }, [emitter]);
    return /* @__PURE__ */ React.createElement(context.Provider, {
      value: emitter
    }, /* @__PURE__ */ React.createElement(Processor, __spreadValues({}, props)), children);
  };
  Provider.propTypes = options.propTypes;
  Provider.defaultProps = options.defaultProps;
  if (process.env.NODE_ENV !== "production") {
    ProcessorInner.displayName = `ScopedModelProcessor(${displayName}.Processor)`;
    Processor.displayName = `ScopedModelProcessor(${displayName}.Processor)`;
    Provider.displayName = `ScopedModel(${displayName})`;
    context.displayName = displayName;
  }
  return {
    context,
    Provider,
    displayName
  };
}

// src/model-factory/create-nullary-model.tsx
var NEVER_UPDATE = () => true;
function createNullaryModel(useModelHook, options) {
  return createModel(useModelHook, {
    displayName: options == null ? void 0 : options.displayName,
    shouldUpdate: NEVER_UPDATE
  });
}

// src/model-factory/create-state-model.tsx
import { useState } from "react";
function createStateModel(initialState, options) {
  return createNullaryModel(() => useState(initialState), options);
}

// src/model-factory/create-reducer-model.tsx
import { useReducer } from "react";
function createReducerModel(reducer, initialState, options) {
  return createNullaryModel(() => useReducer(reducer, initialState), options);
}

// src/model-factory/create-props-selector-model.tsx
function createPropsSelectorModel(options) {
  return createModel((props) => props, options);
}

// src/utils/comparer.ts
function defaultCompare(a, b) {
  return !Object.is(a, b);
}
function compareList(a, b, compare = defaultCompare) {
  if (a === b) {
    return false;
  }
  if (a.length !== b.length) {
    return true;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (compare(a[i], b[i])) {
      return true;
    }
  }
  return false;
}

// src/hooks/useSelector.tsx
import { useDebugValue as useDebugValue2 } from "react";
import {
  useConditionalMemo,
  useSubscription
} from "@lyonph/react-hooks";

// src/hooks/useScopedModelContext.tsx
import { useContext as useContext2 } from "react";
function useScopedModelContext(model) {
  const context = useContext2(model.context);
  if (!context) {
    throw new MissingScopedModelError(model.displayName);
  }
  return context;
}

// src/hooks/useSelector.tsx
function useSelector(model, selector, shouldUpdate = defaultCompare) {
  const notifier = useScopedModelContext(model);
  const sub = useConditionalMemo(() => ({
    read: () => selector(notifier.value),
    subscribe: (callback) => notifier.subscribe(callback),
    shouldUpdate
  }), { notifier, shouldUpdate }, (prev, next) => prev.notifier !== next.notifier || prev.shouldUpdate !== next.shouldUpdate);
  const current = useSubscription(sub);
  useDebugValue2(current);
  return current;
}

// src/hook-factory/create-selector-hook.tsx
function createSelector(model, selector, shouldUpdate = defaultCompare) {
  return () => useSelector(model, selector, shouldUpdate);
}

// src/hooks/useSelectors.tsx
import { useDebugValue as useDebugValue3 } from "react";
import {
  useConditionalCallback
} from "@lyonph/react-hooks";
function useSelectors(model, selector, shouldUpdate = defaultCompare) {
  const compare = useConditionalCallback((a, b) => compareList(a, b, shouldUpdate), shouldUpdate);
  const value = useSelector(model, selector, compare);
  useDebugValue3(value);
  return value;
}

// src/hook-factory/create-selectors-hook.tsx
function createSelectors(model, selector, shouldUpdate = defaultCompare) {
  return () => useSelectors(model, selector, shouldUpdate);
}

// src/hooks/useValue.tsx
import { useDebugValue as useDebugValue4 } from "react";
import {
  useConditionalMemo as useConditionalMemo2,
  useSubscription as useSubscription2
} from "@lyonph/react-hooks";
function useValue(model, shouldUpdate = defaultCompare) {
  const notifier = useScopedModelContext(model);
  const sub = useConditionalMemo2(() => ({
    read: () => notifier.value,
    subscribe: (callback) => notifier.subscribe(callback),
    shouldUpdate
  }), { notifier, shouldUpdate }, (prev, next) => prev.notifier !== next.notifier || prev.shouldUpdate !== next.shouldUpdate);
  const current = useSubscription2(sub);
  useDebugValue4(current);
  return current;
}

// src/hook-factory/create-value-hook.tsx
function createValue(model, shouldUpdate = defaultCompare) {
  return () => useValue(model, shouldUpdate);
}

// src/hooks/useValueOnce.tsx
import { useDebugValue as useDebugValue5 } from "react";
function useValueOnce(model) {
  const { value } = useScopedModelContext(model);
  useDebugValue5(value);
  return value;
}

// src/hook-factory/create-value-once-hook.tsx
function createValueOnce(model) {
  return () => useValueOnce(model);
}

// src/hooks/useSnapshotBase.tsx
import { useEffect as useEffect2 } from "react";
function useSnapshotBase(notifier, listener) {
  useEffect2(() => {
    let mounted = true;
    const callback = (value) => {
      if (mounted) {
        listener(value);
      }
    };
    const unsubscribe = notifier.subscribe(callback);
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [notifier, listener]);
}

// src/hooks/useSnapshot.tsx
function useSnapshot(model, listener) {
  const notifier = useScopedModelContext(model);
  useSnapshotBase(notifier, listener);
}

// src/hook-factory/create-snapshot-hook.tsx
function createSnapshot(model, listener) {
  return () => {
    useSnapshot(model, listener);
  };
}

// src/hooks/useSelectorOnce.tsx
import { useDebugValue as useDebugValue6 } from "react";
import {
  useConditionalMemo as useConditionalMemo3
} from "@lyonph/react-hooks";
function useSelectorOnce(model, selector) {
  const baseValue = useValueOnce(model);
  const value = useConditionalMemo3(() => selector(baseValue), { baseValue, selector }, (prev, next) => prev.baseValue !== next.baseValue || prev.selector !== next.selector);
  useDebugValue6(value);
  return value;
}

// src/hook-factory/create-selector-once-hook.tsx
function createSelectorOnce(model, selector) {
  return () => useSelectorOnce(model, selector);
}

// src/hooks/useScopedModelExists.tsx
import { useContext as useContext3 } from "react";
function useScopedModelExists(model) {
  const context = useContext3(model.context);
  return !!context;
}

// src/index.ts
var src_default = createModel;
export {
  createNullaryModel,
  createPropsSelectorModel,
  createReducerModel,
  createSelector,
  createSelectorOnce,
  createSelectors,
  createSnapshot,
  createStateModel,
  createValue,
  createValueOnce,
  src_default as default,
  useScopedModelExists,
  useSelector,
  useSelectorOnce,
  useSelectors,
  useSnapshot,
  useValue,
  useValueOnce
};
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2021 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2021
 */
//# sourceMappingURL=index.js.map
